// Payout compliance status for the signed-in store
// ────────────────────────────────────────────────────────────────────
// Two sources, deliberately:
//
//   The `taxCompliance` block on the business document is server-written
//   and streams live, so the banner reacts the instant a save lands.
//
//   The getPayoutComplianceStatus callable adds the held-payout totals,
//   which require a query the client is not allowed to run itself.
//
// The live document wins for the pass/fail verdict; the callable supplies
// the dollar amount.

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { useAuth } from './useAuth';
import type {
  MissingTaxRequirement,
  TaxComplianceStatus,
  PayoutComplianceStatus,
} from '../types/business';

export interface PayoutComplianceState {
  loading: boolean;
  /** True only when both the tax record and Stripe are satisfied. */
  payoutEligible: boolean;
  taxComplete: boolean;
  stripeReady: boolean;
  status: TaxComplianceStatus;
  missing: MissingTaxRequirement[];
  stripeBlockers: string[];
  heldCount: number;
  heldTotalCents: number;
  oldestHeldAt: Date | null;
  /** No Stripe account at all — a different prompt applies. */
  needsStripeConnection: boolean;
  /** Non-compliant but still being warned rather than withheld from. */
  inGracePeriod: boolean;
  graceEndsAt: Date | null;
  /** Days remaining before holds begin. Null once they already have. */
  graceDaysLeft: number | null;
  /** Money is actually being withheld right now. */
  holdPayouts: boolean;
  refresh: () => Promise<void>;
}

const EMPTY: Omit<PayoutComplianceState, 'refresh'> = {
  loading: true,
  payoutEligible: true, // assume fine until told otherwise, so no flash of warning
  taxComplete: true,
  stripeReady: true,
  status: 'complete',
  missing: [],
  stripeBlockers: [],
  heldCount: 0,
  heldTotalCents: 0,
  oldestHeldAt: null,
  needsStripeConnection: false,
  inGracePeriod: false,
  graceEndsAt: null,
  graceDaysLeft: null,
  holdPayouts: false,
};

function daysLeftUntil(date: Date | null): number | null {
  if (!date) return null;
  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / 86_400_000));
}

export function usePayoutCompliance(): PayoutComplianceState {
  const { user, userProfile } = useAuth();
  const [state, setState] = useState(EMPTY);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const isBusiness = userProfile?.role === 'business';

  const fetchHeldTotals = useCallback(async () => {
    if (!user || !isBusiness) return;
    try {
      const call = httpsCallable<Record<string, never>, PayoutComplianceStatus>(
        functions,
        'getPayoutComplianceStatus',
      );
      const { data } = await call({});
      if (!mounted.current) return;

      const graceEndsAt = data.graceEndsAt ? new Date(data.graceEndsAt) : null;

      setState((prev) => ({
        ...prev,
        loading: false,
        payoutEligible: data.payoutEligible,
        taxComplete: data.taxComplete,
        stripeReady: data.stripeReady,
        status: data.status,
        missing: data.missing || [],
        stripeBlockers: data.stripeBlockers || [],
        heldCount: data.held?.count ?? 0,
        heldTotalCents: data.held?.totalCents ?? 0,
        oldestHeldAt: data.held?.oldestHeldAt ? new Date(data.held.oldestHeldAt) : null,
        needsStripeConnection: (data.stripeBlockers || []).includes('Connect a Stripe account'),
        inGracePeriod: data.inGracePeriod === true,
        graceEndsAt,
        graceDaysLeft: data.inGracePeriod ? daysLeftUntil(graceEndsAt) : null,
        holdPayouts: data.holdPayouts === true,
      }));
    } catch {
      // A failed status check must never block the dashboard. Leave the
      // last known state and stop the spinner.
      if (mounted.current) setState((prev) => ({ ...prev, loading: false }));
    }
  }, [user, isBusiness]);

  // Live verdict from the business document.
  useEffect(() => {
    if (!user || !isBusiness) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'businesses', user.uid),
      (snap) => {
        if (!mounted.current) return;
        const compliance = snap.data()?.taxCompliance;
        if (!compliance) {
          // Not evaluated yet — the callable below settles it.
          return;
        }
        const graceEndsAt = compliance.graceEndsAt?.toDate?.() ?? null;
        const inGracePeriod = compliance.inGracePeriod === true;

        setState((prev) => ({
          ...prev,
          payoutEligible: compliance.payoutEligible === true,
          taxComplete: compliance.taxComplete === true,
          stripeReady: compliance.stripeReady === true,
          status: compliance.status || 'incomplete',
          missing: compliance.missing || [],
          stripeBlockers: compliance.stripeBlockers || [],
          needsStripeConnection:
            (compliance.stripeBlockers || []).includes('Connect a Stripe account'),
          inGracePeriod,
          graceEndsAt,
          graceDaysLeft: inGracePeriod ? daysLeftUntil(graceEndsAt) : null,
          holdPayouts: compliance.holdPayouts === true,
        }));
      },
      () => { /* permission or network error — callable is the fallback */ },
    );

    return () => unsubscribe();
  }, [user, isBusiness]);

  useEffect(() => { void fetchHeldTotals(); }, [fetchHeldTotals]);

  return { ...state, refresh: fetchHeldTotals };
}
